/**
 * 직원 민감 정보 업데이트 Hook
 *
 * 주민등록번호, 외국인등록번호, 주소 등 민감 정보를 업데이트합니다.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { NationalityType } from '../types/employee';

/**
 * 민감 정보 업데이트 요청
 */
export interface UpdateEmployeeSensitiveInfoRequest {
  employeeId: string;
  nationalityType?: 'domestic' | 'foreign';
  residentRegistrationNo?: string;
  address?: string;
  /** V70.9: 주민등록번호가 실제로 바뀔 때 필수인 변경 사유 */
  reason?: string;
  /** V70.9: 동시 수정 방지 — 편집 시작 시점의 users.updatedAt (ISO) */
  currentUpdatedAt?: string;
}

/** V70.9: 민감정보 업데이트 응답 */
export interface UpdateEmployeeSensitiveInfoResponse {
  success: boolean;
  message: string;
  /** 주민등록번호가 실제로 변경됐는지 */
  rrnChanged?: boolean;
  /** 이어지는 프로필 저장에 사용할 updatedAt 힌트 (서버 기준 ISO) */
  updatedAtHint?: string;
}

/**
 * 민감 정보 업데이트 Hook
 *
 * @returns updateEmployeeSensitiveInfo 함수, 로딩 상태, 에러
 *
 * @example
 * const { updateEmployeeSensitiveInfo, isLoading } = useEmployeeSensitiveInfoMutations();
 *
 * await updateEmployeeSensitiveInfo({
 *   employeeId: 'user123',
 *   nationalityType: 'domestic',
 *   residentRegistrationNo: '123456-1234567',
 *   address: '서울특별시 강남구 테헤란로 00길 12'
 * });
 */
export function useEmployeeSensitiveInfoMutations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const functions = getFunctions();

  /**
   * 직원 민감 정보 업데이트
   */
  const updateEmployeeSensitiveInfo = async (data: UpdateEmployeeSensitiveInfoRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const updateFn = httpsCallable<UpdateEmployeeSensitiveInfoRequest, UpdateEmployeeSensitiveInfoResponse>(
        functions,
        'updateEmployeeSensitiveInfo'
      );
      const result = await updateFn(data);
      setIsLoading(false);
      return result.data;
    } catch (err) {
      console.error('Error updating sensitive info:', err);
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return {
    updateEmployeeSensitiveInfo,
    isLoading,
    error,
  };
}
